import type Highcharts from 'highcharts';
import type { PointerEventObject } from 'highcharts';

import type { DChartInterface } from 'webapp-types';
import { glueDimensionValues } from 'webapp/utils/common';

import type VisualizationSpider from 'webapp/models-custom/visualization/spider';
import BaseRenderer from 'webapp/d-chart/renderer/base';

// a series as built here; its type comes from the chart options. A category the series has no point for is null
type SpiderSeriesOptions = Omit<Highcharts.SeriesAreaOptions, 'type' | 'data' | 'colorIndex'> & {
	data: (DChartInterface.ChartDataPoint | null)[];
	colorIndex?: string | number; // a class for custom colors
};

export default class SpiderRenderer extends BaseRenderer {
	override get visualization(): VisualizationSpider {
		return super.visualization as VisualizationSpider;
	}

	get series() {
		return this.visualization.series;
	}

	get hasLegend() {
		return !this.visualization.isLegendOff;
	}

	get showValues() {
		return this.visualization.showValues;
	}

	override get baseOptions(): Highcharts.Options {
		const that = this;
		let options = super.baseOptions;

		options = this.mergeOptions(options, {
			chart: {
				type: 'area',
				polar: true,
				events: {
					click: function (this: Highcharts.Chart, event: PointerEventObject) {
						if (this.hoverPoint) {
							const { dbxPoint, graphic } = this
								.hoverPoint as DChartInterface.ChartHoverPoint;
							that.onPointClick(dbxPoint, event, graphic?.element);
						}
					},
				},
			},
			pane: {
				size: '90%',
			},
			xAxis: {
				tickmarkPlacement: 'on',
			},
			yAxis: {
				gridLineInterpolation: 'polygon',
				min: 0,
			},
			plotOptions: {
				series: {
					pointPlacement: 'on',
					marker: {
						enabled: false,
						symbol: 'circle',
						radius: 3,
					},
					events: {
						click: function (
							this: Highcharts.Series,
							event: Highcharts.SeriesClickEventObject,
						) {
							that.onPointClick(
								(event.point as DChartInterface.ChartHoverPoint).dbxPoint,
								event,
								(event.point as DChartInterface.ChartHoverPoint).graphic!.element,
							);
						},
					},
				},
			},
		});

		return options;
	}

	override get dataOptions(): Highcharts.Options {
		const categories = this.visualization.categories;

		const options: Omit<Highcharts.Options, 'series'> & { series: SpiderSeriesOptions[] } = {
			series: [],
			legend: {
				enabled: this.hasLegend,
			},
			xAxis: {
				categories,
			},
		};

		const showValues = this.showValues;

		this.series.forEach((series) => {
			const seriesClass = series.points[0]?.onClick ? 'series-clickable' : '';

			const seriesData: SpiderSeriesOptions = {
				className: seriesClass,
				dataLabels: {
					enabled: showValues,
				},
				colorIndex: series.colorIndexOrClass,
				name: series.title,
				data: [],
			};

			// without markers enabled, single points won't be visible
			seriesData.marker = {
				enabled: series.isSingleValue,
			};

			const pointsData: (DChartInterface.ChartDataPoint | null)[] = [];
			categories.forEach((category, idx) => {
				const point = series.points.find(
					(p) => glueDimensionValues(p.metricSelectionData?.dimensionValues) === category,
				);
				if (point) {
					pointsData[idx] = {
						name: point.title,
						y: point.value,
						dbxPoint: point,
					};
				} else {
					pointsData[idx] = null;
				}
			});
			seriesData.data = pointsData;

			options.series.push(seriesData);
		});

		return options;
	}

	override get sizeOptions(): Highcharts.Options {
		let options = super.sizeOptions;

		const m = this.dChart.sizeModifier;

		options = this.mergeOptions(options, {
			chart: {
				spacingTop: 3 + m / 2, // to not cut off the top label
			},
		});

		return options;
	}

	override updateTheme(): void {
		this.visualization.updateColorIndexes();
		super.updateTheme();
	}
}
